import { FC, Dispatch, SetStateAction, useMemo } from 'react';
import _ from 'lodash';
import { useTranslation, TFunction } from '@wix/fe-essentials-standalone/i18n';
import { Filters, FiltersWithoutSearch } from '@common/types';
import { useTemplatesContext } from '@components/Contexts/TemplatesContext';
import { FILTER_TO_KEY_PREFIX_MAP } from '../../TemplateGallery/FilterTags/useFilterTags';
import { FilterModalScreens, IClearAllRecord } from '../TemplateMobileFilters';
import MobileFilterModalLayout from '@components/MobileFilter/MobileFilterModalLayout';
import MobileFilterMenuRow from '@components/MobileFilter/MobileFilterMenuRow';
import { FilterData } from '@components/MobileFilter/MobileFilterMenuRow/MobileFilterMenuRow';
import { ClearAll } from '@utils/route';
import { useTemplateGalleryFilters } from '@components/Hooks/useTemplateGalleryFilters/useTemplateGalleryFilters';
import { useTemplatesBi } from '@components/Hooks/useTemplatesBi';

export const dataHooks = {
  menu: 'templates-mobile-filters-menu-screen',
  row: (filterType: keyof Filters) => `${filterType}-row`,
};

export const FILTER_TO_TITLE_MAP: Record<keyof FiltersWithoutSearch, string> = {
  price: 'marketplace.templates-page.price-type-filter',
  features: 'marketplace.templates-page.features-filter',
  industries: 'marketplace.templates-page.industry-filter',
  languages: 'marketplace.templates-page.languages-filter',
  siteType: 'marketplace.templates-page.site-type-filter',
  styles: 'marketplace.templates-page.styles-filter',
};

interface IFilterMenuScreenProps {
  setCurrentScreen: Dispatch<SetStateAction<FilterModalScreens>>;
  clearAllRecord: IClearAllRecord;
  onClose: () => void;
  onClear: () => void;
}

const TemplatesMobileFiltersMenu: FC<IFilterMenuScreenProps> = ({
  setCurrentScreen,
  clearAllRecord,
  onClose,
  onClear,
}) => {
  const { t } = useTranslation();
  const { totalFilterCount, orderedFilterTypes } = useTemplateGalleryFilters();
  const {
    state: { filters },
  } = useTemplatesContext();
  const { logBiClick } = useTemplatesBi({ is_mobile: true });

  const getFilterRowProps = (
    filterType: keyof FiltersWithoutSearch,
    shouldHideDivider: boolean,
  ): FilterData => {
    return {
      dataHook: dataHooks.row(filterType),
      title: t(FILTER_TO_TITLE_MAP[filterType]),
      onClick: () => {
        logBiClick({ click_type: `${filterType} filter - open` });
        setCurrentScreen(filterType);
      },
      caption: getCaptionText({ filters, filterType, t, clearAllRecord }),
      hideDivider: shouldHideDivider,
    };
  };

  const filterRowProps: Array<FilterData> = orderedFilterTypes.map(
    (type, index, array) => getFilterRowProps(type, index + 1 === array.length),
  );

  return (
    <MobileFilterModalLayout
      dataHook={dataHooks.menu}
      title={t('mobile-filter-menu-title')}
      onClose={onClose}
      onClear={onClear}
      selectedAmount={totalFilterCount}
      content={filterRowProps.map((row) => {
        return <MobileFilterMenuRow key={row.dataHook} filter={row} />;
      })}
    />
  );
};

export default TemplatesMobileFiltersMenu;

interface GetCaptionTextParams {
  filters: Filters;
  filterType: keyof FiltersWithoutSearch;
  t: TFunction;
  clearAllRecord: ClearAll;
}

const getCaptionText = ({
  filters,
  filterType,
  t,
  clearAllRecord,
}: GetCaptionTextParams) => {
  const filterEntry = filters[filterType];
  const wasCleared = clearAllRecord[filterType] || clearAllRecord['all'];
  const prefixKey = FILTER_TO_KEY_PREFIX_MAP[filterType];

  if (!filterEntry || wasCleared) {
    return t(getCombinedKey(prefixKey, 'ALL'));
  }

  if (Array.isArray(filterEntry)) {
    if (filterEntry.length <= 0) {
      return t(getCombinedKey(prefixKey, 'ALL'));
    }

    if (filterEntry.length > 1) {
      return filterEntry.length;
    }

    return t(getCombinedKey(prefixKey, filterEntry[0]));
  }

  return t(getCombinedKey(prefixKey, filterEntry));
};

const getCombinedKey = (prefix: string, key: string) => {
  return `${prefix}${key}`;
};
